import {
  type CardDisputeReason as ApiCardDisputeReason,
  type CardDispute,
  CardDisputeEligibilityReason,
  CardDisputeEvidenceType,
  CardDisputeNextAction,
  CardDisputeStatus,
  CardDisputeWorkflow,
} from '@column/api';

import { formatDate, formatDateTime, formatString } from '~/util';

export type CardDisputeStage = CardDispute['stage'];

export const CARD_DISPUTE_STAGES = [
  'initiated',
  'disputed',
  'merchant_responded',
  'merchant_response_recalled',
  'prearbitration',
  'prearbitration_responded',
  'arbitration',
  'dispute_won',
  'dispute_close',
] as const;

// Ordered by Visa dispute condition, with `other` (never filed with the network) last.
export const CARD_DISPUTE_REASONS = [
  'fraud',
  'no_authorization',
  'incorrect_transaction_code',
  'incorrect_currency',
  'incorrect_account_number',
  'incorrect_amount',
  'duplicate_processing',
  'paid_by_other_means',
  'invalid_data',
  'merchandise_services_not_received',
  'canceled_recurring_transaction',
  'not_as_described',
  'damaged_or_defective_merchandise',
  'quality_of_merchandise_or_services',
  'terms_of_contract',
  'counterfeit_merchandise',
  'misrepresentation',
  'credit_not_processed',
  'canceled_merchandise_or_services',
  'original_credit_not_accepted',
  'non_receipt_cash_at_atm',
  'other',
] as const satisfies readonly `${ApiCardDisputeReason}`[];

/**
 * The dispute reason as a plain string union, so reason logic can switch over string literals. The
 * generated `CardDisputeReason` is a TypeScript enum; convert at the API boundary with `toApiReason`.
 */
export type CardDisputeReason = (typeof CARD_DISPUTE_REASONS)[number];

export const toApiReason = (reason: CardDisputeReason): ApiCardDisputeReason => reason as ApiCardDisputeReason;

export interface CardDisputeReasonGroup {
  label: string;
  reasons: CardDisputeReason[];
}

/** Reasons grouped by Visa dispute category, for the reason picker. */
export const CARD_DISPUTE_REASON_GROUPS: CardDisputeReasonGroup[] = [
  { label: 'Fraud', reasons: ['fraud'] },
  { label: 'Authorization', reasons: ['no_authorization'] },
  {
    label: 'Processing errors',
    reasons: [
      'incorrect_transaction_code',
      'incorrect_currency',
      'incorrect_account_number',
      'incorrect_amount',
      'duplicate_processing',
      'paid_by_other_means',
      'invalid_data',
    ],
  },
  {
    label: 'Consumer disputes',
    reasons: [
      'merchandise_services_not_received',
      'canceled_recurring_transaction',
      'not_as_described',
      'damaged_or_defective_merchandise',
      'quality_of_merchandise_or_services',
      'terms_of_contract',
      'counterfeit_merchandise',
      'misrepresentation',
      'credit_not_processed',
      'canceled_merchandise_or_services',
      'original_credit_not_accepted',
      'non_receipt_cash_at_atm',
    ],
  },
  { label: 'Not filed with the Card Network', reasons: ['other'] },
];

export const CARD_DISPUTE_SUBMISSION_STATUSES = [
  'not_required',
  'draft',
  'pending_submission',
  'submitted',
  'requires_action',
  'failed',
] as const;

export type CardDisputeSubmissionStatus = (typeof CARD_DISPUTE_SUBMISSION_STATUSES)[number];

export const CARD_DISPUTE_REASON_LABELS: Record<CardDisputeReason, string> = {
  fraud: 'Fraud',
  no_authorization: 'No authorization',
  incorrect_transaction_code: 'Incorrect transaction code',
  incorrect_currency: 'Incorrect currency',
  incorrect_account_number: 'Incorrect account number',
  incorrect_amount: 'Incorrect amount',
  duplicate_processing: 'Duplicate processing',
  paid_by_other_means: 'Paid by other means',
  invalid_data: 'Invalid data',
  merchandise_services_not_received: 'Merchandise or services not received',
  canceled_recurring_transaction: 'Canceled recurring transaction',
  not_as_described: 'Not as described',
  damaged_or_defective_merchandise: 'Damaged or defective merchandise',
  quality_of_merchandise_or_services: 'Quality of merchandise or services',
  terms_of_contract: 'Terms of contract',
  counterfeit_merchandise: 'Counterfeit merchandise',
  misrepresentation: 'Misrepresentation',
  credit_not_processed: 'Credit not processed',
  canceled_merchandise_or_services: 'Canceled merchandise or services',
  original_credit_not_accepted: 'Original credit not accepted',
  non_receipt_cash_at_atm: 'Non-receipt of cash at ATM',
  other: 'Other',
};

export const getDisputeReasonLabel = (reason: string): string =>
  (CARD_DISPUTE_REASON_LABELS as Record<string, string>)[reason] ?? reason;

export const CARD_DISPUTE_STAGE_LABELS: Record<CardDisputeStage, string> = {
  initiated: 'Initiated',
  disputed: 'Disputed',
  merchant_responded: 'Merchant responded',
  merchant_response_recalled: 'Merchant response recalled',
  prearbitration: 'Pre-arbitration',
  prearbitration_responded: 'Pre-arbitration responded',
  arbitration: 'Arbitration',
  dispute_won: 'Dispute won',
  dispute_close: 'Dispute closed',
};

export const CARD_DISPUTE_STAGE_BADGE_CLASSES: Record<CardDisputeStage, string> = {
  initiated:
    'border-gray-700/10 bg-gray-100/60 text-gray-700 dark:border-gray-400/20 dark:bg-gray-500/15 dark:text-gray-300',
  disputed:
    'border-blue-700/10 bg-blue-100/50 text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/15 dark:text-blue-300',
  merchant_responded:
    'border-cyan-700/10 bg-cyan-100/50 text-cyan-700 dark:border-cyan-400/20 dark:bg-cyan-500/15 dark:text-cyan-300',
  merchant_response_recalled:
    'border-cyan-700/10 bg-cyan-100/50 text-cyan-700 dark:border-cyan-400/20 dark:bg-cyan-500/15 dark:text-cyan-300',
  prearbitration:
    'border-orange-700/10 bg-orange-100/50 text-orange-700 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300',
  prearbitration_responded:
    'border-cyan-700/10 bg-cyan-100/50 text-cyan-700 dark:border-cyan-400/20 dark:bg-cyan-500/15 dark:text-cyan-300',
  arbitration:
    'border-purple-700/10 bg-purple-100/50 text-purple-700 dark:border-purple-400/20 dark:bg-purple-500/15 dark:text-purple-300',
  dispute_won:
    'border-green-700/10 bg-green-100/50 text-green-700 dark:border-green-400/20 dark:bg-green-500/15 dark:text-green-300',
  dispute_close:
    'border-gray-700/10 bg-gray-100/60 text-gray-600 dark:border-gray-400/20 dark:bg-gray-500/15 dark:text-gray-300',
};

export const CARD_DISPUTE_STATUS_LABELS: Record<CardDisputeStatus, string> = {
  [CardDisputeStatus.OPEN]: 'Open',
  [CardDisputeStatus.REQUIRES_ACTION]: 'Action required',
  [CardDisputeStatus.MANUAL_REVIEW_REQUIRED]: 'Manual review required',
  [CardDisputeStatus.CLOSED]: 'Closed',
};

export const CARD_DISPUTE_STATUS_BADGE_CLASSES: Record<CardDisputeStatus, string> = {
  [CardDisputeStatus.OPEN]:
    'border-gray-700/10 bg-gray-100/60 text-gray-700 dark:border-gray-400/20 dark:bg-gray-500/15 dark:text-gray-300',
  [CardDisputeStatus.REQUIRES_ACTION]:
    'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300',
  [CardDisputeStatus.MANUAL_REVIEW_REQUIRED]:
    'border-purple-700/10 bg-purple-100/50 text-purple-700 dark:border-purple-400/20 dark:bg-purple-500/15 dark:text-purple-300',
  [CardDisputeStatus.CLOSED]:
    'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300',
};

export const CARD_DISPUTE_SUBMISSION_STATUS_BADGE_CLASSES: Record<CardDisputeSubmissionStatus, string> = {
  not_required:
    'border-gray-700/10 bg-gray-100/60 text-gray-700 dark:border-gray-400/20 dark:bg-gray-500/15 dark:text-gray-300',
  draft:
    'border-gray-700/10 bg-gray-100/60 text-gray-700 dark:border-gray-400/20 dark:bg-gray-500/15 dark:text-gray-300',
  pending_submission:
    'border-blue-700/10 bg-blue-100/50 text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/15 dark:text-blue-300',
  submitted:
    'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300',
  requires_action:
    'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300',
  failed: 'border-red-700/10 bg-red-200/50 text-red-600/80 dark:border-red-400/20 dark:bg-red-500/15 dark:text-red-300',
};

export const CARD_DISPUTE_SUBMISSION_STATUS_LABELS: Record<CardDisputeSubmissionStatus, string> = {
  not_required: 'Submission not required',
  draft: 'Draft',
  pending_submission: 'Pending submission',
  submitted: 'Submitted',
  requires_action: 'Waiting on your response',
  failed: 'Submission failed',
};

export const CARD_DISPUTE_WORKFLOW_LABELS: Record<CardDisputeWorkflow, string> = {
  [CardDisputeWorkflow.COLLABORATION]: 'Collaboration',
  [CardDisputeWorkflow.ALLOCATION]: 'Allocation',
};

export const getDisputeStageLabel = (stage: string): string =>
  (CARD_DISPUTE_STAGE_LABELS as Record<string, string>)[stage] ?? formatString(stage);

export const getDisputeStageBadgeClasses = (stage: string): string =>
  CARD_DISPUTE_STAGE_BADGE_CLASSES[stage as CardDisputeStage] ?? '';

export const getDisputeStatusLabel = (status: string): string =>
  (CARD_DISPUTE_STATUS_LABELS as Record<string, string>)[status] ?? formatString(status);

export const getDisputeStatusBadgeClasses = (status: string): string =>
  CARD_DISPUTE_STATUS_BADGE_CLASSES[status as CardDisputeStatus] ?? '';

export const getSubmissionStatusLabel = (submissionStatus: string): string =>
  (CARD_DISPUTE_SUBMISSION_STATUS_LABELS as Record<string, string>)[submissionStatus] ?? formatString(submissionStatus);

export const getSubmissionStatusBadgeClasses = (submissionStatus: string): string =>
  CARD_DISPUTE_SUBMISSION_STATUS_BADGE_CLASSES[submissionStatus as CardDisputeSubmissionStatus] ?? '';

export const getWorkflowLabel = (workflow: string): string =>
  (CARD_DISPUTE_WORKFLOW_LABELS as Record<string, string>)[workflow] ?? formatString(workflow);

export const CARD_DISPUTE_RESOLUTION_REASONS = [
  'merchant_accepted',
  'rapid_dispute_resolution',
  'arbitration_network_ruling',
  'issuer_liability',
  'exceeded_filing_date',
  'customer_accepted_liability',
] as const;

export type CardDisputeResolutionReason = (typeof CARD_DISPUTE_RESOLUTION_REASONS)[number];

export const CARD_DISPUTE_RESOLUTION_REASON_LABELS: Record<CardDisputeResolutionReason, string> = {
  merchant_accepted: 'Dispute won — merchant accepted liability',
  rapid_dispute_resolution: 'Dispute won — rapid dispute resolution',
  arbitration_network_ruling: 'Resolved by arbitration ruling',
  issuer_liability: 'Closed — issuer liability',
  exceeded_filing_date: 'Closed — filing window expired',
  customer_accepted_liability: 'Platform accepted the cardholder claim — funded by the platform',
};

export const getDisputeResolutionLabel = (resolutionReason?: string | null, stage?: CardDisputeStage): string => {
  if (resolutionReason === 'arbitration_network_ruling') {
    if (stage === 'dispute_won') return 'Card Network arbitration ruling in issuer favor';
    if (stage === 'dispute_close') return 'Card Network arbitration ruling in acquirer favor';
  }
  if (resolutionReason) {
    const knownLabel = (CARD_DISPUTE_RESOLUTION_REASON_LABELS as Record<string, string>)[resolutionReason];
    if (knownLabel) return knownLabel;
  }
  if (stage === 'dispute_won' || stage === 'dispute_close') return 'Outcome details unavailable';
  return (CARD_DISPUTE_STAGE_LABELS as Record<string, string>)[stage ?? ''] ?? (stage || '');
};

export const CARD_DISPUTE_EVIDENCE_TYPE_LABELS: Record<CardDisputeEvidenceType, string> = {
  [CardDisputeEvidenceType.CARDHOLDER_LETTER]: 'Cardholder letter',
  [CardDisputeEvidenceType.MERCHANT_LETTER]: 'Merchant letter',
  [CardDisputeEvidenceType.SALES_DRAFT]: 'Sales draft',
  [CardDisputeEvidenceType.TE_FOLIO]: 'TE folio',
  [CardDisputeEvidenceType.CREDIT_VOUCHER]: 'Credit voucher',
  [CardDisputeEvidenceType.SHIPPING_RECEIPT]: 'Shipping receipt',
  [CardDisputeEvidenceType.PROOF_OF_DELIVERY]: 'Proof of delivery',
  [CardDisputeEvidenceType.BANK_STATEMENT]: 'Bank statement',
  [CardDisputeEvidenceType.CANCELLED_CHECK]: 'Cancelled check',
  [CardDisputeEvidenceType.SECOND_OPINION]: 'Second opinion',
  [CardDisputeEvidenceType.FULFILLMENT]: 'Fulfillment',
  [CardDisputeEvidenceType.AUTHORIZATION_RECORD]: 'Authorization record',
  [CardDisputeEvidenceType.NETWORK_DOCUMENT]: 'Network document',
  [CardDisputeEvidenceType.UPDATED_CARDHOLDER_LETTER]: 'Updated cardholder letter',
  [CardDisputeEvidenceType.UPDATED_MERCHANT_LETTER]: 'Updated merchant letter',
  [CardDisputeEvidenceType.AFFIDAVIT_OF_FRAUD]: 'Affidavit of fraud',
  [CardDisputeEvidenceType.OTHER]: 'Other',
  [CardDisputeEvidenceType.NETWORK_EXHIBIT]: 'Network exhibit',
  [CardDisputeEvidenceType.ISSUER_CERTIFICATION]: 'Issuer certification',
  [CardDisputeEvidenceType.TRANSACTION_RECEIPT]: 'Transaction receipt',
};

export const CARD_DISPUTE_ELIGIBILITY_REASON_LABELS: Record<CardDisputeEligibilityReason, string> = {
  [CardDisputeEligibilityReason.TRANSACTION_NOT_FOUND]: 'Transaction not found',
  [CardDisputeEligibilityReason.TRANSACTION_NOT_SETTLED]: 'Transaction not settled',
  [CardDisputeEligibilityReason.CURRENCY_MISMATCH]: 'Currency mismatch',
  [CardDisputeEligibilityReason.AMOUNT_EXCEEDS_SETTLED_AMOUNT]: 'Amount exceeds the settled transaction amount',
  [CardDisputeEligibilityReason.AMOUNT_EXCEEDS_RECEIPT_DIFFERENCE]: 'Amount exceeds the receipt difference',
  [CardDisputeEligibilityReason.AMOUNT_NOT_PARTIAL]: 'This reason can only dispute part of the transaction amount',
  [CardDisputeEligibilityReason.ACTIVE_DISPUTE_EXISTS]: 'An active dispute already exists for this transaction',
  [CardDisputeEligibilityReason.EXCEEDED_FILING_WINDOW]: 'The Card Network filing window has passed',
  [CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE]: 'Missing required evidence',
  [CardDisputeEligibilityReason.REASON_NOT_APPLICABLE_TO_TRANSACTION]: 'This reason does not apply to this transaction',
};

export interface CardDisputeNextActionCopy {
  label: string;
  description: string;
  ctaLabel?: string;
}

export const CARD_DISPUTE_NEXT_ACTION_COPY: Record<CardDisputeNextAction, CardDisputeNextActionCopy> = {
  [CardDisputeNextAction.SUBMIT]: {
    label: 'Ready to submit',
    description: 'This dispute is a draft. Submit it to the Card Network to start the chargeback process.',
    ctaLabel: 'Submit dispute',
  },
  [CardDisputeNextAction.ADD_EVIDENCE]: {
    label: 'Add evidence',
    description: 'The Card Network is waiting on additional evidence before this dispute can move forward.',
    ctaLabel: 'Add evidence',
  },
  [CardDisputeNextAction.AWAIT_NETWORK_SUBMISSION]: {
    label: 'Submitting to the Card Network',
    description: 'Column is submitting this dispute to the Card Network. This usually takes a few minutes.',
  },
  [CardDisputeNextAction.AWAIT_NETWORK_RESPONSE]: {
    label: 'Awaiting network response',
    description:
      'The Card Network is reviewing this dispute. Column checks the Card Network for updates about every 30 minutes.',
  },
  [CardDisputeNextAction.AWAIT_MANUAL_ARBITRATION]: {
    label: 'In arbitration',
    description:
      'Arbitration is awaiting Column operations. Filing is not yet confirmed; rulings can take several weeks.',
  },
  [CardDisputeNextAction.RESOLVE_REQUIRED_ACTION]: {
    label: 'Action required',
    description: 'The Card Network is waiting on a required action from you. Resolve it to keep this dispute moving.',
  },
  [CardDisputeNextAction.RESPOND_TO_PREARBITRATION]: {
    label: 'Pre-arbitration response needed',
    description: 'Review the pre-arbitration response and the issuer actions available for this dispute.',
    ctaLabel: 'Review pre-arbitration',
  },
  [CardDisputeNextAction.RESPOND_TO_MERCHANT_RESPONSE]: {
    label: 'Merchant responded',
    description: 'Review the merchant response and the issuer actions available for this dispute.',
    ctaLabel: 'Review response',
  },
};

export const DISPUTE_SYNC_NOTE = 'Column checks the Card Network for updates about every 30 minutes.';

// respond_by is serialized as a plain date (no time or zone). Rendering it as a local datetime
// shifts the displayed day in zones behind UTC, so date-only values render as a UTC calendar date.
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const formatRespondBy = (respondBy: string | undefined): string => {
  if (!respondBy) {
    return '—';
  }
  if (DATE_ONLY_PATTERN.test(respondBy)) {
    return formatDate(respondBy, { timeZoneFormat: 'utc' });
  }
  return formatDateTime(respondBy, { timeZoneFormat: 'utc' });
};
